import { FiletreeSelectReact } from '@shared/ui-react/FiletreeSelectReact';
import { makeObservable, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { AlignmentStore } from '@shared/alignment/AlignmentStore';

@observer
export class ActiveAlignmentDropdown extends React.Component<{
    filetreeData: ITreeData;
    store?: {
        // store for active alignment
        activeAlignment: {
            modelId: number;
            index: number;
        };
    };
    alignmentStore: AlignmentStore; // store to handle alignment indexes
    storageKey: string;

    // controlled component
    selectedModelId?: number;
    selectedIndex?: number;
    onChange?: (modelId: number, index: number) => void;
}> {
    @observable.ref treeData: ITreeData;
    private lastCacheKey = '';
    constructor(props) {
        super(props);
        makeObservable(this);
    }
    init() {
        if (!this.props.filetreeData) return;
        // Create a simple cache key from the data that matters
        const alignments = this.props.alignmentStore.alignmentIndexes;
        const cacheKey = `${this.props.filetreeData?.id}_${alignments.size}_${alignments.values().next().value}`;
        // Only transform if something changed
        if (cacheKey !== this.lastCacheKey) {
            this.lastCacheKey = cacheKey;
            this.treeData = this.turnAlignmentsIntoFolders(this.props.filetreeData);
        }
    }

    componentDidMount() {
        this.init();
    }

    componentDidUpdate() {
        this.init();
    }

    render() {
        void this.props.alignmentStore.alignmentIndexes.size;

        if (!this.treeData) return <div className='loading-inline'></div>;

        let id = null;
        const propsId = this.props.selectedModelId || this.props.store?.activeAlignment?.modelId;
        const propsIndex = this.props.selectedIndex || this.props.store?.activeAlignment?.index;
        if (propsId) {
            id = propsId + '';
            if (id == '-1') {
                id = null;
            } else if (propsIndex) {
                id += '_' + propsIndex;
            }
        }
        return (
            <FiletreeSelectReact
                formFieldName='activeAlignment'
                selectable='file'
                treeData={this.treeData}
                selectedId={id}
                hideEmptyFolders={true}
                canClearValue={true}
                showSelectedIcon={false}
                hideNonPresentable={true}
                onChange={this.handleChange}
                childFilter={this.childFilter}
                storageKey={this.props.storageKey}
            />
        );
    }

    handleChange = (idWithIndex: string) => {
        if (idWithIndex) {
            const [id, index] = idWithIndex.split('_');
            runInAction(() => {
                const safeId = parseInt(id);
                const safeIndex = parseInt(index) || 0;
                if (typeof this.props.onChange === 'function') {
                    this.props.onChange(safeId, safeIndex);
                } else if (this.props.store) {
                    this.props.store.activeAlignment.modelId = parseInt(id);
                    this.props.store.activeAlignment.index = parseInt(index) || 0;
                }
            });
        } else {
            if (typeof this.props.onChange === 'function') {
                this.props.onChange(-1, 0);
            } else {
                runInAction(() => {
                    this.props.store.activeAlignment.index = 0;
                    this.props.store.activeAlignment.modelId = -1;
                });
            }
        }
    };

    childFilter = item => {
        if (item.tolerance) return true;
        return kuuraModel.isAlignment(item);
    };

    turnAlignmentsIntoFolders = (treeData: ITreeData): any => {
        const alignmentStore = this.props.alignmentStore;
        const visit = node => {
            if (node && node.models && node.models.length) {
                for (const model of node.models) {
                    if (model.layerCounts && model.layerCounts.alignments > 1) {
                        // Check if this specific model uses legacy lookup
                        const isLegacy = alignmentStore.isModelLegacy(model.id);
                        const models = [];
                        for (let i = 0; i < model.layerCounts.alignments; i++) {
                            const name = Array.isArray(model.alignmentNames) ? model.alignmentNames[i] : null;

                            let lookupKey: string;
                            if (isLegacy) {
                                // Legacy implementation: simple index-based lookup
                                lookupKey = i == 0 ? `${model.id}` : `${model.id}_${i}`;
                            } else {
                                // New implementation: base64-encoded name-based lookup
                                const base64Name = name ? btoa(encodeURIComponent(name)) : btoa(`index_${i}`);
                                lookupKey = `${model.id}_${base64Name}`;
                            }

                            const nodeIndex = alignmentStore.getAlignmentIndex(lookupKey);
                            if (nodeIndex !== undefined) {
                                models.push({
                                    name: name ?? model.name + ' #' + (i + 1),
                                    id: nodeIndex === 0 ? model.id + '' : model.id + '_' + nodeIndex,
                                    uuid: nodeIndex === 0 ? model.id + '' : model.id + '_' + nodeIndex,
                                    filesize: 1,
                                    layerCounts: {
                                        alignments: 1,
                                    },
                                    _sortIndex: nodeIndex,
                                });
                            }
                        }

                        if (models.length > 0) {
                            const folder = {
                                id: 'alignment_' + model.id,
                                name: model.name,
                                folders: [],
                                models: [],
                                tolerance: {},
                            };
                            models.sort((a, b) => a._sortIndex - b._sortIndex);

                            folder.models = models;
                            node.folders.push(folder);
                        }
                    }
                }

                node.models = node.models.filter(m => m.layerCounts && m.layerCounts.alignments <= 1);
            }

            if (node && node.folders) {
                for (const child of node.folders) {
                    visit(child);
                }
            }
        };

        if (!treeData) return null;
        const clone = JSON.parse(JSON.stringify(treeData));
        visit(clone);
        return clone;
    };
}
